import { useConfirm } from '../components/ConfirmProvider';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { rosterApi, clientsApi, adminApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { LogoHome } from '../components/LogoHome';
import { MultiPickList } from '../components/MultiPickList';
import { ManualEntryDialog } from '../components/ManualEntryDialog';
import { ResolveShiftDialog } from '../components/ResolveShiftDialog';
import { manualEntriesApi } from '../services/api';
import { utcToSydLocalInput, sydLocalInputToUtcIso, sydDateKey, sydTodayKey } from '../utils/sydneyTime';
import {
  Calendar, Plus, Check, X, ArrowLeft, LogOut, Users, Clock,
  ChevronLeft, ChevronRight, AlertTriangle, Repeat, User, Trash2, Play, CheckCircle2, Wand2, Loader2, Copy, Pencil, UsersRound, StickyNote, ClipboardCheck
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

const SHIFT_TYPES = [
  { value: 'personal_care', label: 'Personal Care' },
  { value: 'community_access', label: 'Community Access' },
  { value: 'domestic_assistance', label: 'Domestic Assistance' },
  { value: 'overnight', label: 'Overnight' },
  { value: 'sleepover', label: 'Sleepover' },
  { value: 'group', label: 'Group' },
  { value: 'transport', label: 'Transport' },
  { value: 'other', label: 'Other' },
];

const TYPE_COLORS = {
  personal_care: 'bg-blue-100 text-blue-700 border-blue-200',
  community_access: 'bg-purple-100 text-purple-700 border-purple-200',
  domestic_assistance: 'bg-amber-100 text-amber-700 border-amber-200',
  overnight: 'bg-slate-200 text-slate-700 border-slate-300',
  sleepover: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  group: 'bg-teal-100 text-teal-700 border-teal-200',
  transport: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  other: 'bg-slate-100 text-slate-600 border-slate-200',
};

const STATUS_COLORS = {
  open: 'bg-amber-100 text-amber-700 border-amber-200',
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  accepted: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  declined: 'bg-red-100 text-red-700 border-red-200',
  completed: 'bg-slate-100 text-slate-600 border-slate-200',
  cancelled: 'bg-slate-200 text-slate-500 border-slate-300',
  missed: 'bg-rose-600 text-white border-rose-700',
  in_progress: 'bg-teal-100 text-teal-700 border-teal-200',
};

// A past shift the worker never clocked into (or flagged missed) and not yet resolved
function needsResolution(s) {
  if (s.missed_resolution || s.manual_entry_id) return false;
  if (s.status === 'missed') return true;
  return ['scheduled', 'accepted'].includes(s.status) && new Date(s.end_at) < new Date();
}

const SLOT_LABELS = {
  day: 'Day',
  afternoon: 'Afternoon',
  night: 'Night',
  sleepover: 'Sleepover',
};

const SLOT_COLORS = {
  day: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  afternoon: 'bg-orange-100 text-orange-800 border-orange-200',
  night: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  sleepover: 'bg-purple-100 text-purple-800 border-purple-200',
};

// Derive slot on the client so legacy shifts render correctly before the backend backfill lands
function deriveSlot(shift) {
  if (shift.shift_slot) return shift.shift_slot;
  if (shift.shift_type === 'sleepover') return 'sleepover';
  try {
    const start = new Date(shift.start_at);
    const aestHour = parseInt(start.toLocaleString('en-AU', { timeZone: 'Australia/Sydney', hour: 'numeric', hour12: false }), 10);
    if (aestHour >= 6 && aestHour < 14) return 'day';
    if (aestHour >= 14 && aestHour < 22) return 'afternoon';
    return 'night';
  } catch {
    return 'day';
  }
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function fmtDateTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon = 0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

// Return YYYY-MM-DD in LOCAL time (avoids UTC off-by-one for users east of UTC like Sydney)
// Sydney 'today' as a plain calendar Date (local midnight) so week navigation matches AEST
function sydToday() {
  const [y, m, d] = sydTodayKey().split('-').map(Number);
  return new Date(y, m - 1, d);
}

function localISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function RosterPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isScheduler = user?.role === 'organisation' || user?.role === 'supervisor';

  const [shifts, setShifts] = useState([]);
  const [slotFilter, setSlotFilter] = useState('all');
  const [openShifts, setOpenShifts] = useState([]);
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewDate, setViewDate] = useState(startOfWeek(sydToday()));
  const [filterWorker, setFilterWorker] = useState('');
  const [filterClient, setFilterClient] = useState('');

  // Create shift dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingShift, setEditingShift] = useState(null);
  const [applyToGroup, setApplyToGroup] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    client_ids: [], worker_ids: [], start_at: '', end_at: '',
    shift_type: 'personal_care', sil_flag: false, break_minutes: 0, notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recDays, setRecDays] = useState([]);
  const [recWeeks, setRecWeeks] = useState(4);

  // Availability
  const [availability, setAvailability] = useState([]);
  // Auto-fill
  const [autoFillOpen, setAutoFillOpen] = useState(false);
  const [autoFillPreview, setAutoFillPreview] = useState(null);
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const [autoFillApplying, setAutoFillApplying] = useState(false);
  const [copyingWeek, setCopyingWeek] = useState(false);
  const confirm = useConfirm();
  // Missed-shift resolution & manual hours
  const [resolveTarget, setResolveTarget] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualPreset, setManualPreset] = useState(null);
  const [myManual, setMyManual] = useState([]);

  const loadMyManual = useCallback(async () => {
    if (user?.role !== 'worker') return;
    try { const { data } = await manualEntriesApi.list(); setMyManual(data || []); } catch { /* silent */ }
  }, [user?.role]);
  useEffect(() => { loadMyManual(); }, [loadMyManual]);

  const openManualFor = (shift) => {
    setManualPreset(shift ? {
      worker_id: shift.worker_id, client_ids: shift.client_ids?.length ? shift.client_ids : (shift.client_id ? [shift.client_id] : []),
      clock_in: shift.start_at, clock_out: shift.end_at, break_minutes: shift.break_minutes || 0, roster_shift_id: shift.id,
    } : null);
    setManualOpen(true);
  };

  const backRoute = user?.role === 'organisation' ? '/admin' : user?.role === 'supervisor' ? '/supervisor' : '/dashboard';

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const weekEnd = new Date(viewDate); weekEnd.setDate(weekEnd.getDate() + 6);
      const [shiftsRes, clientsRes, openRes, statsRes, availRes, usersRes] = await Promise.all([
        rosterApi.listShifts({
          date_from: localISODate(viewDate),
          date_to: localISODate(weekEnd),
          worker_id: filterWorker || undefined,
          client_id: filterClient || undefined,
        }),
        clientsApi.getAll(),
        user?.role === 'worker' ? rosterApi.openShifts().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        isScheduler ? rosterApi.getStats().catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        user?.role === 'worker' ? rosterApi.getMyAvailability().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        isScheduler ? adminApi.getUsers().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setShifts(shiftsRes.data || []);
      setClients(clientsRes.data || []);
      setOpenShifts(openRes.data || []);
      setStats(statsRes.data);
      setAvailability(availRes.data || []);
      setWorkers((usersRes.data || []).filter(u => ['worker', 'supervisor'].includes(u.role)));
    } catch (err) {
      toast.error('Failed to load roster');
    } finally {
      setLoading(false);
    }
  }, [viewDate, filterWorker, filterClient, user?.role, isScheduler]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleLogout = () => { logout(); navigate('/login'); };

  // Group shifts by day (applies the slot filter)
  const shiftsByDay = useMemo(() => {
    const map = {};
    for (let i = 0; i < 7; i++) {
      const d = new Date(viewDate); d.setDate(d.getDate() + i);
      map[localISODate(d)] = [];
    }
    for (const s of shifts) {
      if (slotFilter !== 'all' && deriveSlot(s) !== slotFilter) continue;
      // s.start_at is a UTC ISO string — convert to local date first so a
      // 10pm UTC shift correctly lands on the next-day column in Sydney.
      const key = sydDateKey(s.start_at);
      if (map[key]) map[key].push(s);
    }
    return map;
  }, [shifts, viewDate, slotFilter]);

  // Counts per slot for the pill buttons (based on the current week's shifts, ignoring the filter)
  const slotCounts = useMemo(() => {
    const weekStart = new Date(viewDate);
    const weekEnd = new Date(viewDate); weekEnd.setDate(weekEnd.getDate() + 7);
    const c = { all: 0, day: 0, afternoon: 0, night: 0, sleepover: 0 };
    for (const s of shifts) {
      const d = new Date(s.start_at);
      if (d < weekStart || d >= weekEnd) continue;
      c.all += 1;
      c[deriveSlot(s)] = (c[deriveSlot(s)] || 0) + 1;
    }
    return c;
  }, [shifts, viewDate]);

  const navigateWeek = (direction) => {
    const d = new Date(viewDate);
    d.setDate(d.getDate() + direction * 7);
    setViewDate(d);
  };

  const openCreate = (presetDate) => {
    setEditingId(null);
    setEditingShift(null);
    // datetime-local input expects LOCAL "YYYY-MM-DDTHH:MM"
    const today = presetDate || utcToSydLocalInput(new Date());
    setForm({
      client_ids: [], worker_ids: [],
      start_at: today, end_at: today,
      shift_type: 'personal_care', sil_flag: false, break_minutes: 0, notes: ''
    });
    setIsRecurring(false); setRecDays([]); setRecWeeks(4);
    setCreateOpen(true);
  };

  const openEdit = (shift) => {
    setEditingId(shift.id);
    setEditingShift(shift);
    setApplyToGroup(true);
    // Convert stored UTC ISO to local datetime-local format
    const coWorkerIds = (shift.co_workers || []).map(c => c.id).filter(Boolean);
    setForm({
      client_ids: shift.client_ids?.length ? shift.client_ids : (shift.client_id ? [shift.client_id] : []),
      worker_ids: [...(shift.worker_id ? [shift.worker_id] : []), ...coWorkerIds],
      start_at: utcToSydLocalInput(shift.start_at),
      end_at: utcToSydLocalInput(shift.end_at),
      shift_type: shift.shift_type || 'personal_care',
      sil_flag: !!shift.sil_flag,
      break_minutes: shift.break_minutes || 0,
      notes: shift.notes || '',
    });
    setIsRecurring(false); setRecDays([]); setRecWeeks(4);
    setCreateOpen(true);
  };

  // When multiple participants are picked, default the type to Group
  const setClientIds = (ids) => {
    setForm(f => ({
      ...f,
      client_ids: ids,
      shift_type: ids.length > 1 && f.shift_type === 'personal_care' ? 'group' : f.shift_type,
    }));
  };

  const handleCreate = async () => {
    if (form.client_ids.length === 0) { toast.error('Please select at least one participant'); return; }
    if (!form.start_at || !form.end_at) { toast.error('Start and end required'); return; }
    if (form.end_at <= form.start_at) { toast.error('End must be after start'); return; }
    setSaving(true);
    try {
      if (isRecurring) {
        if (recDays.length === 0) { toast.error('Pick at least one day for recurring'); setSaving(false); return; }
        const { data } = await rosterApi.createRecurring({
          client_ids: form.client_ids,
          worker_ids: form.worker_ids,
          days_of_week: recDays,
          start_time: form.start_at.slice(11, 16),
          end_time: form.end_at.slice(11, 16),
          shift_type: form.shift_type,
          sil_flag: form.sil_flag,
          break_minutes: parseInt(form.break_minutes) || 0,
          notes: form.notes || null,
          start_date: form.start_at.slice(0, 10),
          weeks: recWeeks,
        });
        toast.success(`Recurring series created — ${data.count} roster entries over ${recWeeks} weeks`);
      } else if (editingId) {
        const coWorkerIds = (editingShift?.co_workers || []).map(c => c.id).filter(Boolean);
        const original = editingShift?.worker_id || '';
        const keep = form.worker_ids.includes(original) ? original : (form.worker_ids.find(id => !coWorkerIds.includes(id)) || '');
        const extras = form.worker_ids.filter(id => id !== keep && !coWorkerIds.includes(id));
        await rosterApi.updateShift(editingId, {
          client_ids: form.client_ids,
          worker_id: keep,
          start_at: sydLocalInputToUtcIso(form.start_at),
          end_at: sydLocalInputToUtcIso(form.end_at),
          shift_type: form.shift_type,
          sil_flag: form.sil_flag,
          break_minutes: parseInt(form.break_minutes) || 0,
          notes: form.notes || null,
          apply_to_group: (editingShift?.group_size || 1) > 1 ? applyToGroup : false,
        });
        for (const wid of extras) {
          await rosterApi.addWorkerToShift(editingId, wid);
        }
        toast.success(extras.length ? `Shift updated · ${extras.length} worker${extras.length > 1 ? 's' : ''} added` : 'Shift updated');
      } else {
        const { data } = await rosterApi.createShift({
          client_ids: form.client_ids,
          worker_ids: form.worker_ids,
          start_at: sydLocalInputToUtcIso(form.start_at),
          end_at: sydLocalInputToUtcIso(form.end_at),
          shift_type: form.shift_type,
          sil_flag: form.sil_flag,
          break_minutes: parseInt(form.break_minutes) || 0,
          notes: form.notes || null,
        });
        const n = data?.created_shifts?.length || 1;
        toast.success(n > 1 ? `Team shift created for ${n} workers` : form.worker_ids.length === 0 ? 'Open shift added to the pool' : 'Shift created');
      }
      setCreateOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create shift');
    } finally {
      setSaving(false);
    }
  };

  const handleAccept = async (id) => {
    try { await rosterApi.acceptShift(id); toast.success('Shift accepted'); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleDecline = async (id) => {
    const reason = await confirm({ title: 'Decline this shift?', description: 'It goes back to the open pool for someone else to claim.', withInput: true, inputLabel: 'Reason (optional)', confirmText: 'Decline shift', destructive: true });
    if (reason === null) return;
    try { await rosterApi.declineShift(id, reason); toast.success('Shift declined'); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleClaim = async (id) => {
    try { await rosterApi.claimShift(id); toast.success('Shift claimed!'); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleDelete = (s) => setDeleteTarget(s);
  const confirmDelete = async (applyToGroupFlag) => {
    if (!deleteTarget) return;
    try {
      const { data } = await rosterApi.deleteShift(deleteTarget.id, applyToGroupFlag);
      toast.success(applyToGroupFlag ? `Team shift removed (${data.deleted} workers)` : (deleteTarget.group_size || 1) > 1 ? 'Removed this worker from the shift' : 'Shift deleted');
      setDeleteTarget(null);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete shift'); }
  };
  const handleClockIn = async (id) => {
    try { await rosterApi.clockInFromShift(id); toast.success('Clocked in!'); navigate('/dashboard'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Availability
  const toggleAvailDay = (dayIdx) => {
    const existing = availability.find(a => a.day_of_week === dayIdx);
    if (existing) {
      setAvailability(availability.filter(a => a.day_of_week !== dayIdx));
    } else {
      setAvailability([...availability, { day_of_week: dayIdx, start_time: '09:00', end_time: '17:00', is_available: true }]);
    }
  };
  const updateAvailSlot = (dayIdx, field, value) => {
    setAvailability(availability.map(a => a.day_of_week === dayIdx ? { ...a, [field]: value } : a));
  };
  const saveAvailability = async () => {
    try {
      await rosterApi.setMyAvailability(availability);
      toast.success('Availability saved');
    } catch { toast.error('Failed to save'); }
  };

  const runAutoFillPreview = async () => {
    setAutoFillLoading(true);
    setAutoFillOpen(true);
    setAutoFillPreview(null);
    try {
      const res = await rosterApi.autoFill(true);
      setAutoFillPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
      setAutoFillOpen(false);
    } finally {
      setAutoFillLoading(false);
    }
  };

  const handleCopyLastWeek = async () => {
    // Copy from the week immediately preceding the currently-viewed week
    const sourceStart = new Date(viewDate);
    sourceStart.setDate(sourceStart.getDate() - 7);
    const sourceLabel = sourceStart.toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short' });
    const targetLabel = viewDate.toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short' });

    const ok = await confirm({
      title: `Copy week of ${sourceLabel} → week of ${targetLabel}?`,
      description: 'Worker, client, times, type and notes are preserved. Shifts that already exist on the target week are skipped.',
      confirmText: 'Copy week',
    });
    if (!ok) return;

    setCopyingWeek(true);
    try {
      const { data } = await rosterApi.copyWeek(localISODate(sourceStart), localISODate(viewDate));
      if (data.copied === 0 && data.skipped === 0) {
        toast.info(data.message || 'No shifts found in the previous week.');
      } else {
        toast.success(`Copied ${data.copied} shift${data.copied !== 1 ? 's' : ''}${data.skipped ? ` · skipped ${data.skipped} duplicate${data.skipped !== 1 ? 's' : ''}` : ''}`);
        loadAll();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Copy failed');
    } finally {
      setCopyingWeek(false);
    }
  };

  const applyAutoFill = async () => {
    setAutoFillApplying(true);
    try {
      const res = await rosterApi.autoFill(false);
      toast.success(`Auto-filled ${res.data.filled} of ${res.data.checked} open shifts`);
      setAutoFillOpen(false);
      setAutoFillPreview(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-fill failed');
    } finally {
      setAutoFillApplying(false);
    }
  };

  const teamMeta = (s) => {
    const nClients = s.client_ids?.length || (s.client_id ? 1 : 0);
    const nWorkers = s.group_size || 1;
    return {
      nClients, nWorkers,
      isTeam: nWorkers > 1,
      isGroup: nClients > 1,
      clientLabel: s.client_names?.length ? s.client_names.join(', ') : (s.client_name || '—'),
      coWorkerLabel: (s.co_workers || []).map(c => c.name || 'Open slot').join(', '),
    };
  };

  const renderShiftCard = (s) => {
    const canAct = user?.role === 'worker' && s.worker_id === user?.id;
    const isOpen = !s.worker_id && s.status === 'open';
    const meta = teamMeta(s);
    return (
      <div
        key={s.id}
        className={`p-2.5 rounded-lg border text-xs bg-white hover:shadow-sm transition-all ${s.has_conflict ? 'border-rose-300 ring-1 ring-rose-200' : meta.isTeam ? 'border-teal-300 border-l-4' : 'border-slate-200'}`}
        data-testid={`shift-${s.id}`}
      >
        <div className="flex items-center justify-between gap-1 mb-1">
          <span className="font-semibold text-slate-900">{fmtTime(s.start_at)} → {fmtTime(s.end_at)}</span>
          <div className="flex items-center gap-1">
            {meta.isTeam && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-teal-600 text-white px-1.5 py-px text-[9px] font-semibold" title={`${meta.nWorkers} workers : ${meta.nClients} participant${meta.nClients > 1 ? 's' : ''}`} data-testid={`ratio-${s.id}`}>
                <UsersRound className="h-2.5 w-2.5" />{meta.nWorkers}:{meta.nClients}
              </span>
            )}
            {s.has_conflict && <AlertTriangle className="h-3 w-3 text-rose-500" title="Time conflict" />}
          </div>
        </div>
        <p className="text-slate-800 font-medium truncate" title={meta.clientLabel}>
          {meta.isGroup && <Users className="inline h-3 w-3 mr-1 text-teal-600" />}{meta.clientLabel}
        </p>
        <p className="text-slate-500 truncate">{s.worker_name || <em className="text-amber-600">Open shift</em>}</p>
        {meta.isTeam && meta.coWorkerLabel && (
          <p className="text-teal-700 truncate" title={meta.coWorkerLabel} data-testid={`co-workers-${s.id}`}>with {meta.coWorkerLabel}</p>
        )}
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
          <Badge variant="outline" className={`text-[9px] ${SLOT_COLORS[deriveSlot(s)] || ''}`} title={SLOT_LABELS[deriveSlot(s)]}>
            {SLOT_LABELS[deriveSlot(s)]}
          </Badge>
          <Badge variant="outline" className={`text-[9px] ${TYPE_COLORS[s.shift_type] || ''}`}>
            {SHIFT_TYPES.find(t => t.value === s.shift_type)?.label || s.shift_type}
          </Badge>
          <Badge variant="outline" className={`text-[9px] capitalize ${STATUS_COLORS[s.status] || ''}`}>{s.status}</Badge>
          {s.sil_flag && <Badge className="text-[9px] bg-indigo-600">SIL</Badge>}
          {s.auto_notes?.length > 0 && (
            <span className="inline-flex items-center text-slate-400 hover:text-teal-600 cursor-help" title={s.auto_notes.map(n => `${new Date(n.at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} — ${n.by_name}: ${n.text}`).join('\n')} data-testid={`notes-${s.id}`}>
              <StickyNote className="h-3 w-3" /><span className="text-[9px] ml-0.5">{s.auto_notes.length}</span>
            </span>
          )}
          {s.missed_resolution && s.missed_resolution !== 'pending_manual' && (
            <Badge variant="outline" className="text-[9px] bg-white text-slate-600 border-slate-300 capitalize" data-testid={`resolution-${s.id}`}>{s.missed_resolution.replace(/_/g, ' ')}</Badge>
          )}
          {s.missed_resolution === 'pending_manual' && (
            <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-300" data-testid={`resolution-${s.id}`}>hours pending approval</Badge>
          )}
        </div>
        <div className="flex items-center gap-1 mt-2">
          {isScheduler && needsResolution(s) && (
            <Button size="sm" className="h-6 text-[10px] px-2 bg-rose-600 hover:bg-rose-700" onClick={() => setResolveTarget(s)} data-testid={`resolve-${s.id}`}>
              <ClipboardCheck className="h-2.5 w-2.5 mr-0.5" /> Resolve
            </Button>
          )}
          {canAct && needsResolution(s) && (
            <Button size="sm" className="h-6 text-[10px] px-2 bg-amber-600 hover:bg-amber-700" onClick={() => openManualFor(s)} data-testid={`log-hours-${s.id}`}>
              <ClipboardCheck className="h-2.5 w-2.5 mr-0.5" /> Log hours
            </Button>
          )}
          {canAct && s.status === 'scheduled' && (
            <>
              <Button size="sm" className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleAccept(s.id)} data-testid={`accept-${s.id}`}>Accept</Button>
              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => handleDecline(s.id)} data-testid={`decline-${s.id}`}>Decline</Button>
            </>
          )}
          {canAct && ['scheduled', 'accepted'].includes(s.status) && new Date(s.start_at) <= new Date(Date.now() + 2 * 3600 * 1000) && new Date(s.end_at) >= new Date() && (
            <Button size="sm" className="h-6 text-[10px] px-2 bg-teal-600 hover:bg-teal-700" onClick={() => handleClockIn(s.id)} data-testid={`clock-in-${s.id}`}>
              <Play className="h-2.5 w-2.5 mr-0.5" /> Clock In
            </Button>
          )}
          {user?.role === 'worker' && isOpen && (
            <Button size="sm" className="h-6 text-[10px] px-2 bg-amber-600 hover:bg-amber-700" onClick={() => handleClaim(s.id)} data-testid={`claim-${s.id}`}>Claim</Button>
          )}
          {isScheduler && (
            <div className="flex items-center gap-0.5 ml-auto">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-slate-400 hover:text-teal-600"
                onClick={() => openEdit(s)}
                data-testid={`edit-shift-${s.id}`}
                title="Edit shift"
              >
                <Pencil className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" onClick={() => handleDelete(s)} data-testid={`delete-${s.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="roster-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoHome className="h-10 w-auto" />
            <div className="hidden sm:block">
              <h1 className="font-semibold text-slate-900 text-sm">Roster</h1>
              <p className="text-xs text-slate-500">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to={backRoute} className="text-sm text-slate-500 hover:text-teal-600 flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Link>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats row (scheduler) */}
        {isScheduler && stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500">This Week</p><p className="text-2xl font-bold">{stats.this_week}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Upcoming</p><p className="text-2xl font-bold">{stats.total_upcoming}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Open Pool</p><p className="text-2xl font-bold text-amber-600">{stats.open_shifts}</p></CardContent></Card>
            <Card className={stats.conflicts > 0 ? 'border-rose-300' : ''}>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500">Conflicts</p>
                <p className={`text-2xl font-bold ${stats.conflicts > 0 ? 'text-rose-600' : ''}`}>{stats.conflicts}</p>
              </CardContent>
            </Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Declined</p><p className="text-2xl font-bold text-slate-700">{stats.declined}</p></CardContent></Card>
          </div>
        )}

        <Tabs defaultValue="calendar" className="space-y-4">
          <TabsList className="bg-white border border-slate-200 flex-wrap">
            <TabsTrigger value="calendar" data-testid="tab-calendar">Weekly Calendar</TabsTrigger>
            <TabsTrigger value="list" data-testid="tab-list">List</TabsTrigger>
            {user?.role === 'worker' && <TabsTrigger value="open" data-testid="tab-open">Open Shifts ({openShifts.length})</TabsTrigger>}
            {user?.role === 'worker' && <TabsTrigger value="availability" data-testid="tab-availability">My Availability</TabsTrigger>}
            {user?.role === 'worker' && <TabsTrigger value="manual" data-testid="tab-manual">Manual Hours{myManual.filter(m => m.approval_status === 'pending').length > 0 ? ` (${myManual.filter(m => m.approval_status === 'pending').length} pending)` : ''}</TabsTrigger>}
          </TabsList>

          {/* Worker: manual hours history */}
          {user?.role === 'worker' && (
            <TabsContent value="manual">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Manually logged hours</CardTitle>
                  <CardDescription>Hours you've logged for shifts you forgot to clock into. Pending entries count toward pay once a supervisor approves them.</CardDescription>
                </CardHeader>
                <CardContent>
                  {myManual.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">No manual entries yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {myManual.map(m => (
                        <div key={m.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${m.approval_status === 'pending' ? 'bg-amber-50 border-amber-200' : m.approval_status === 'rejected' ? 'bg-slate-50 border-slate-200' : 'bg-emerald-50/40 border-emerald-200'}`} data-testid={`my-manual-${m.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-900">{fmtDateTime(m.clock_in)} → {fmtTime(m.clock_out)} <span className="text-slate-500 font-normal">· {m.total_hours}h</span></div>
                            <div className="text-xs text-slate-500 italic truncate">“{m.manual_reason}”</div>
                            {m.approval_status === 'rejected' && m.rejected_reason && <div className="text-xs text-rose-600 mt-0.5">Rejected: {m.rejected_reason}</div>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className={`text-[10px] capitalize ${m.approval_status === 'pending' ? 'bg-amber-500' : m.approval_status === 'rejected' ? 'bg-slate-400' : 'bg-emerald-600'}`}>{m.approval_status}</Badge>
                            {m.approval_status === 'pending' && (
                              <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 hover:text-rose-600" onClick={async () => { try { await manualEntriesApi.remove(m.id); toast.success('Withdrawn'); loadMyManual(); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } }} data-testid={`withdraw-manual-${m.id}`}>Withdraw</Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Calendar view */}
          <TabsContent value="calendar">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)} data-testid="prev-week"><ChevronLeft className="h-4 w-4" /></Button>
                    <CardTitle className="text-base sm:text-lg">
                      Week of {viewDate.toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'long' })}
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} data-testid="next-week"><ChevronRight className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setViewDate(startOfWeek(sydToday()))}>Today</Button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {isScheduler && (
                      <>
                        <Select value={filterWorker} onValueChange={(v) => setFilterWorker(v === 'all' ? '' : v)}>
                          <SelectTrigger className="w-full sm:w-[180px] h-9" data-testid="filter-worker"><SelectValue placeholder="All workers" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All workers</SelectItem>
                            {workers.map(w => (
                              <SelectItem key={w.id} value={w.id}>
                                {w.name}
                                {w.role === 'supervisor' && <span className="ml-2 text-[10px] text-indigo-600 uppercase font-medium">sup</span>}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={filterClient} onValueChange={(v) => setFilterClient(v === 'all' ? '' : v)}>
                          <SelectTrigger className="w-full sm:w-[180px] h-9" data-testid="filter-client"><SelectValue placeholder="All clients" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All clients</SelectItem>
                            {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                    {isScheduler && (
                      <Button variant="outline" onClick={handleCopyLastWeek} disabled={copyingWeek} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="copy-last-week-btn">
                        <Copy className="h-4 w-4 mr-1" />
                        {copyingWeek ? 'Copying...' : 'Copy Last Week'}
                      </Button>
                    )}
                    {isScheduler && (
                      <Button variant="outline" onClick={runAutoFillPreview} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="auto-fill-btn">
                        <Wand2 className="h-4 w-4 mr-1" /> Auto-Fill Open
                      </Button>
                    )}
                    {isScheduler && (
                      <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => openCreate()} data-testid="create-shift-btn">
                        <Plus className="h-4 w-4 mr-1" /> New Shift
                      </Button>
                    )}
                    {user?.role === 'worker' && (
                      <Button variant="outline" onClick={() => openManualFor(null)} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="log-hours-btn">
                        <ClipboardCheck className="h-4 w-4 mr-1" /> Log hours manually
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Shift-slot filter pills */}
                <div className="flex flex-wrap gap-2 mb-4" data-testid="roster-slot-filter">
                  {[
                    { key: 'all', label: 'All shifts', color: 'bg-slate-800 hover:bg-slate-900 text-white' },
                    { key: 'day', label: 'Day', color: 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border border-yellow-200' },
                    { key: 'afternoon', label: 'Afternoon', color: 'bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-200' },
                    { key: 'night', label: 'Night', color: 'bg-indigo-100 hover:bg-indigo-200 text-indigo-800 border border-indigo-200' },
                    { key: 'sleepover', label: 'Sleepover', color: 'bg-purple-100 hover:bg-purple-200 text-purple-800 border border-purple-200' },
                  ].map(p => {
                    const active = slotFilter === p.key;
                    const count = slotCounts[p.key] || 0;
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => setSlotFilter(p.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          active
                            ? `${p.color} ring-2 ring-offset-1 ring-teal-500 shadow-sm`
                            : `${p.color} opacity-60 hover:opacity-100`
                        }`}
                        data-testid={`slot-filter-${p.key}`}
                      >
                        {p.label}
                        <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] rounded-full ${active ? 'bg-white/30' : 'bg-black/10'}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                  {slotFilter !== 'all' && slotCounts[slotFilter] === 0 && (
                    <span className="text-xs text-slate-400 self-center ml-2">No {slotFilter} shifts this week</span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="roster-week-grid">
                  {Object.entries(shiftsByDay).map(([date, dayShifts], i) => {
                    // Parse "YYYY-MM-DD" as LOCAL date (avoid UTC midnight shifting day)
                    const [yy, mm, dd] = date.split('-').map(Number);
                    const d = new Date(yy, mm - 1, dd);
                    const isToday = date === sydTodayKey();
                    return (
                      <div key={date} className={`border rounded-lg p-2 min-h-[80px] lg:min-h-[240px] ${isToday ? 'bg-teal-50 border-teal-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`day-column-${date}`}>
                        <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-slate-200">
                          <div className="flex items-baseline gap-1.5 lg:block">
                            <p className="text-[10px] text-slate-500 uppercase">{DAYS[i]}</p>
                            <p className={`text-sm font-semibold ${isToday ? 'text-teal-700' : 'text-slate-700'}`}>{d.getDate()}<span className="lg:hidden text-slate-400 font-normal"> {d.toLocaleDateString('en-AU', { month: 'short' })}</span></p>
                          </div>
                          {isScheduler && (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openCreate(`${date}T09:00`)}>
                              <Plus className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          {dayShifts.length === 0 ? (
                            <p className="text-xs text-slate-400 text-center py-4">No shifts</p>
                          ) : (
                            dayShifts.map(renderShiftCard)
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* List view */}
          <TabsContent value="list">
            <Card>
              <CardHeader><CardTitle className="text-base">All Shifts This Week</CardTitle></CardHeader>
              <CardContent>
                {shifts.length === 0 ? (
                  <p className="text-slate-400 text-center py-8">No shifts scheduled this week.</p>
                ) : (
                  <div className="space-y-2">
                    {shifts.sort((a, b) => a.start_at.localeCompare(b.start_at)).map(s => (
                      <div key={s.id} className={`flex items-center justify-between p-3 rounded-lg border ${s.has_conflict ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200'}`} data-testid={`list-shift-${s.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-900">{fmtDateTime(s.start_at)}</span>
                            <span className="text-slate-400">→</span>
                            <span className="text-slate-700">{fmtTime(s.end_at)}</span>
                            {s.has_conflict && <Badge className="bg-rose-100 text-rose-700 border border-rose-200 text-[9px]"><AlertTriangle className="h-2.5 w-2.5 mr-0.5" />Conflict</Badge>}
                          </div>
                          <p className="text-sm text-slate-600">
                            <User className="inline h-3 w-3 mr-1" />{s.worker_name || <em className="text-amber-600">Open</em>}
                            {(s.group_size || 1) > 1 && s.co_workers?.length > 0 && (
                              <span className="text-teal-700"> + {s.co_workers.map(c => c.name || 'open slot').join(', ')}</span>
                            )}
                            <span className="mx-2 text-slate-300">·</span>
                            <Users className="inline h-3 w-3 mr-1" />{teamMeta(s).clientLabel}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {(s.group_size || 1) > 1 && (
                            <Badge className="bg-teal-600 text-white text-[10px]"><UsersRound className="h-2.5 w-2.5 mr-0.5" />{teamMeta(s).nWorkers}:{teamMeta(s).nClients}</Badge>
                          )}
                          <Badge variant="outline" className={`text-[10px] ${SLOT_COLORS[deriveSlot(s)] || ''}`}>{SLOT_LABELS[deriveSlot(s)]}</Badge>
                          <Badge variant="outline" className={`text-[10px] ${TYPE_COLORS[s.shift_type] || ''}`}>{SHIFT_TYPES.find(t => t.value === s.shift_type)?.label}</Badge>
                          <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_COLORS[s.status] || ''}`}>{s.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Open shifts (worker) */}
          {user?.role === 'worker' && (
            <TabsContent value="open">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Open Shift Pool</CardTitle>
                  <CardDescription>Pick up shifts that need covering.</CardDescription>
                </CardHeader>
                <CardContent>
                  {openShifts.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">No open shifts at the moment.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {openShifts.map(s => (
                        <div key={s.id} className="p-4 rounded-lg border border-amber-200 bg-amber-50/50" data-testid={`open-shift-${s.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-slate-900">{fmtDateTime(s.start_at)} → {fmtTime(s.end_at)}</span>
                            <Badge className={`${TYPE_COLORS[s.shift_type] || ''} border`}>{SHIFT_TYPES.find(t => t.value === s.shift_type)?.label}</Badge>
                          </div>
                          <p className="text-sm text-slate-700 mb-1">Participant{(s.client_ids?.length || 1) > 1 ? 's' : ''}: <strong>{teamMeta(s).clientLabel}</strong></p>
                          {(s.group_size || 1) > 1 && (
                            <p className="text-xs text-teal-700 mb-1">Team shift · {teamMeta(s).nWorkers}:{teamMeta(s).nClients}{teamMeta(s).coWorkerLabel ? ` · with ${teamMeta(s).coWorkerLabel}` : ''}</p>
                          )}
                          {s.notes && <p className="text-xs text-slate-500 mb-2 italic">&ldquo;{s.notes}&rdquo;</p>}
                          <Button className="bg-amber-600 hover:bg-amber-700 w-full" onClick={() => handleClaim(s.id)} data-testid={`claim-open-${s.id}`}>
                            <Check className="h-4 w-4 mr-1" /> Claim This Shift
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Availability (worker) */}
          {user?.role === 'worker' && (
            <TabsContent value="availability">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Weekly Availability</CardTitle>
                  <CardDescription>Let your organisation know when you&apos;re free to work.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {DAYS.map((day, idx) => {
                    const slot = availability.find(a => a.day_of_week === idx);
                    return (
                      <div key={day} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`avail-${day}`}>
                        <div className="w-14"><strong>{day}</strong></div>
                        <Switch checked={!!slot} onCheckedChange={() => toggleAvailDay(idx)} data-testid={`avail-toggle-${day}`} />
                        {slot ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Input type="time" value={slot.start_time} onChange={(e) => updateAvailSlot(idx, 'start_time', e.target.value)} className="h-8 w-28" />
                            <span className="text-slate-400">to</span>
                            <Input type="time" value={slot.end_time} onChange={(e) => updateAvailSlot(idx, 'end_time', e.target.value)} className="h-8 w-28" />
                          </div>
                        ) : <span className="text-slate-400 text-sm flex-1">Unavailable</span>}
                      </div>
                    );
                  })}
                  <Button onClick={saveAvailability} className="bg-teal-600 hover:bg-teal-700 w-full sm:w-auto mt-2" data-testid="save-availability-btn">
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Save Availability
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Create Shift Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-teal-600" />
              {editingId ? (editingShift?.group_size > 1 ? 'Edit Team Shift' : 'Edit Shift') : isRecurring ? 'New Recurring Shift Series' : form.worker_ids.length > 1 ? 'New Team Shift' : form.client_ids.length > 1 ? 'New Group Outing' : 'New Shift'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!editingId && (
              <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg">
                <Switch checked={isRecurring} onCheckedChange={setIsRecurring} id="recurring" data-testid="toggle-recurring" />
                <Label htmlFor="recurring" className="text-sm flex items-center gap-1 cursor-pointer">
                  <Repeat className="h-3.5 w-3.5" /> Recurring Weekly
                </Label>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Participant{form.client_ids.length > 1 ? 's' : ''}</Label>
                {form.client_ids.length > 1 && <span className="text-[10px] font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">Group outing · {form.client_ids.length}</span>}
              </div>
              <MultiPickList
                items={clients.map(c => ({ id: c.id, name: c.name }))}
                selected={form.client_ids}
                onChange={setClientIds}
                placeholder="Search participants…"
                emptyLabel="Pick one participant, or several for a group outing"
                testId="form-clients"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm">Worker{form.worker_ids.length > 1 ? 's' : ''} {form.worker_ids.length === 0 && <span className="text-slate-400">(none = open pool)</span>}</Label>
                {form.worker_ids.length > 0 && (
                  <span className="text-[10px] font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5" data-testid="form-ratio">
                    {form.worker_ids.length}:{Math.max(form.client_ids.length, 1)} support ratio
                  </span>
                )}
              </div>
              <MultiPickList
                items={workers.map(w => {
                  const alreadyOn = editingId && (editingShift?.co_workers || []).some(c => c.id === w.id);
                  return { id: w.id, name: w.name, hint: w.role === 'supervisor' ? 'sup' : undefined, disabled: !!alreadyOn, disabledLabel: 'already on team' };
                })}
                selected={form.worker_ids}
                onChange={(ids) => setForm({ ...form, worker_ids: ids })}
                placeholder="Search workers…"
                emptyLabel="Open pool — any worker can claim"
                testId="form-workers"
              />
              {editingId && (editingShift?.group_size || 1) > 1 && (
                <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 p-2 rounded-lg mt-2">
                  <Switch checked={applyToGroup} onCheckedChange={setApplyToGroup} id="apply-group" data-testid="toggle-apply-group" />
                  <Label htmlFor="apply-group" className="text-xs cursor-pointer">
                    Apply time / participant / notes changes to all {editingShift.group_size} workers on this team shift
                  </Label>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-sm">{isRecurring ? 'First day & start time' : 'Start'}</Label>
                <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="mt-1" data-testid="form-start" />
              </div>
              <div>
                <Label className="text-sm">End</Label>
                <Input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} className="mt-1" data-testid="form-end" />
              </div>
            </div>

            {isRecurring && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 space-y-2">
                <Label className="text-sm font-medium">Repeat on</Label>
                <div className="flex gap-1">
                  {DAYS.map((day, idx) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => setRecDays(recDays.includes(idx) ? recDays.filter(d => d !== idx) : [...recDays, idx])}
                      className={`flex-1 py-1.5 text-xs rounded border ${recDays.includes(idx) ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-300'}`}
                      data-testid={`rec-day-${day}`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">For</Label>
                  <Input type="number" min={1} max={26} value={recWeeks} onChange={(e) => setRecWeeks(parseInt(e.target.value) || 1)} className="w-20 h-8" />
                  <span className="text-sm text-slate-600">week(s)</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-sm">Shift Type</Label>
                <Select value={form.shift_type} onValueChange={(v) => setForm({ ...form, shift_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SHIFT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Break (minutes)</Label>
                <Input type="number" min={0} value={form.break_minutes} onChange={(e) => setForm({ ...form, break_minutes: e.target.value })} className="mt-1" />
              </div>
            </div>

            <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-lg">
              <Switch checked={form.sil_flag} onCheckedChange={(v) => setForm({ ...form, sil_flag: v })} id="sil" />
              <Label htmlFor="sil" className="text-sm cursor-pointer">SIL (Supported Independent Living)</Label>
            </div>

            <div>
              <Label className="text-sm">Notes (optional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" placeholder="Shift instructions, specific needs..." />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-shift-btn">
                {saving ? 'Saving...' : (editingId ? 'Save changes' : isRecurring ? 'Create Series' : form.worker_ids.length > 1 ? `Create Team Shift (${form.worker_ids.length})` : 'Create Shift')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ResolveShiftDialog shift={resolveTarget} open={!!resolveTarget} onOpenChange={(o) => !o && setResolveTarget(null)} onResolved={loadAll} />
      <ManualEntryDialog open={manualOpen} onOpenChange={setManualOpen} isScheduler={isScheduler} workers={workers} clients={clients} preset={manualPreset} onSaved={() => { loadAll(); loadMyManual(); }} />

      {/* Delete confirmation (single + team shifts) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-shift-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-rose-600" /> {(deleteTarget?.group_size || 1) > 1 ? 'Remove from team shift?' : 'Delete this shift?'}
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (deleteTarget.group_size || 1) > 1 && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600">
                <strong>{deleteTarget.worker_name || 'Open slot'}</strong> is one of <strong>{deleteTarget.group_size}</strong> workers rostered
                {' '}{fmtDateTime(deleteTarget.start_at)} → {fmtTime(deleteTarget.end_at)} for {teamMeta(deleteTarget).clientLabel}.
              </p>
              <div className="grid gap-2">
                <Button variant="outline" onClick={() => confirmDelete(false)} data-testid="delete-this-only-btn">
                  Remove just this worker
                </Button>
                <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => confirmDelete(true)} data-testid="delete-whole-team-btn">
                  Delete whole team shift ({deleteTarget.group_size} workers)
                </Button>
                <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              </div>
            </div>
          )}
          {deleteTarget && (deleteTarget.group_size || 1) <= 1 && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600">
                <strong>{deleteTarget.worker_name || 'Open shift'}</strong> · {fmtDateTime(deleteTarget.start_at)} → {fmtTime(deleteTarget.end_at)} for {teamMeta(deleteTarget).clientLabel}.
                {deleteTarget.recurring_parent_id && <span className="block text-xs text-slate-500 mt-1">Only this occurrence is removed — the rest of the recurring series stays.</span>}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="delete-cancel-btn">Cancel</Button>
                <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => confirmDelete(false)} data-testid="delete-confirm-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Delete shift
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Auto-Fill Preview Dialog */}
      <Dialog open={autoFillOpen} onOpenChange={setAutoFillOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-amber-600" />
              Smart Auto-Fill — Preview
            </DialogTitle>
          </DialogHeader>
          {autoFillLoading ? (
            <div className="flex flex-col items-center py-10 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
              <p className="text-sm text-slate-600">Analysing availability & conflicts...</p>
            </div>
          ) : autoFillPreview ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900">
                    {autoFillPreview.checked} open shift{autoFillPreview.checked !== 1 ? 's' : ''} checked
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    {autoFillPreview.suggestions.filter(s => s.chosen_worker).length} can be auto-assigned
                    · {autoFillPreview.suggestions.filter(s => !s.chosen_worker).length} need manual review
                  </p>
                </div>
              </div>

              {autoFillPreview.suggestions.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No open shifts to fill right now. 🎉
                </div>
              ) : (
                <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
                  {autoFillPreview.suggestions.map((s, i) => (
                    <div
                      key={s.shift_id}
                      className={`p-3 rounded-lg border text-sm ${s.chosen_worker ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}
                      data-testid={`auto-fill-row-${i}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">
                            {fmtDateTime(s.start_at)} → {fmtTime(s.end_at)}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {clients.find(c => c.id === s.client_id)?.name || '—'}
                          </p>
                        </div>
                        {s.chosen_worker ? (
                          <div className="text-right">
                            <Badge className="bg-emerald-600 text-white">
                              <Check className="h-3 w-3 mr-0.5" /> {s.chosen_worker.name}
                            </Badge>
                            <p className="text-[10px] text-emerald-700 mt-0.5">{s.reason}</p>
                          </div>
                        ) : (
                          <Badge variant="outline" className="bg-white text-slate-500 border-slate-300">
                            <X className="h-3 w-3 mr-0.5" /> Unmatched
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setAutoFillOpen(false)} disabled={autoFillApplying}>
                  Cancel
                </Button>
                <Button
                  onClick={applyAutoFill}
                  disabled={autoFillApplying || autoFillPreview.suggestions.filter(s => s.chosen_worker).length === 0}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="apply-auto-fill-btn"
                >
                  {autoFillApplying ? (
                    <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Applying...</>
                  ) : (
                    <><Wand2 className="h-4 w-4 mr-1" /> Apply {autoFillPreview.suggestions.filter(s => s.chosen_worker).length} Assignment{autoFillPreview.suggestions.filter(s => s.chosen_worker).length !== 1 ? 's' : ''}</>
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
